import React, { useMemo } from 'react';
import { EstimateOutput, Year } from '../../../api/models/EstimateOutput';
import Box from '../../../components/box/box';
import ChartHeader from './chart-header';
import DataTile from '../../../components/data-card/data-tile';
import { IconType } from '../../../components/icons/icon';
import Grid from '../../../components/grid/grid';
import { colors } from '../../../Theme';
import './overview-chart.scss';

interface InvestorOverviewChartProps {
  estimateOutput: EstimateOutput;
  height?: number;
  setActiveStep: (step: number) => void;
}

function OverviewChart({
  estimateOutput,
  height,
  setActiveStep,
}: InvestorOverviewChartProps) {
  const downPayment: number = useMemo(() => {
    return (
      estimateOutput.years[0].losses.find(
        (loss: { subject: string; amount: number }) =>
          loss.subject === 'downPayment'
      )?.amount || 0
    );
  }, [estimateOutput]);

  const upfrontCosts: number = useMemo(() => {
    return Math.round(
      estimateOutput.years[0].losses.reduce(
        (sum: number, loss: { amount: number }) => sum + loss.amount,
        0
      ) - (downPayment || 0)
    );
  }, [estimateOutput, downPayment]);

  const perMonthCashFlow: number = useMemo(() => {
    return Math.round(
      estimateOutput.years.slice(1, 6).reduce((sum: number, year: Year) => {
        const rentalIncome =
          year.gains.find((g) => g.subject === 'rentalIncome')?.amount || 0;
        return sum + (rentalIncome - year.yearSummary.lossesTotal);
      }, 0) /
        (5 * 12) || 0
    );
  }, [estimateOutput]);

  const perMonthRentNeeded: number = useMemo(() => {
    return Math.round(
      estimateOutput.years.slice(1, 6).reduce((sum: number, year: Year) => {
        return sum + year.yearSummary.lossesTotal;
      }, 0) /
        (5 * 12) || 0
    );
  }, [estimateOutput]);

  const perMonthGain: number = useMemo(() => {
    return Math.round(
      estimateOutput.years
        .slice(1, 6)
        .reduce(
          (sum: number, year: Year) => sum + year.yearSummary.gainsTotal,
          0
        ) /
        (5 * 12) || 0
    );
  }, [estimateOutput]);

  const breakEvenYear: string = useMemo(() => {
    for (const year of estimateOutput.years) {
      if (year.sale.netProfit > 0 && year.year !== 0) {
        return 'Year ' + year.year;
      }
    }

    return 'Never';
  }, [estimateOutput]);

  return (
    <Box
      style={{
        height: `calc(${height}px - 1rem)`,
        display: 'flex',
        flexDirection: 'column',
        padding: '1rem',
      }}
    >
      <ChartHeader title="Investor Overview" className="text-input-primary" />

      <Grid
        container
        style={{
          flexGrow: 1,
          overflow: 'hidden',
          height: '100%',
          paddingTop: '1rem',
          paddingBottom: '1rem',
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <Grid item xs={3} className="tile-cont">
          <DataTile
            iconVariant={IconType.CreditScoreIcon}
            iconColor="white"
            title={`$${perMonthCashFlow.toLocaleString()}`}
            subtitle="Cash Flow Per Month"
            description={`Expect to cash flow an average of $${perMonthCashFlow.toLocaleString()} amount per month for the first 5 years.`}
            actionText="Go to graph"
            backgroundColor={colors.Dark1Color}
            iconBackgroundColor="#a6c0ed"
            onClickActionText={() => setActiveStep(2)}
            className="bg-dark-tertiary text-input-primary"
            iconClassName="icon-accent"
          />
        </Grid>

        <Grid item xs={3} className="tile-cont">
          <DataTile
            iconVariant={IconType.Calendar}
            iconColor="white"
            title={breakEvenYear}
            subtitle="Break Even Year"
            description={
              breakEvenYear === 'Never'
                ? 'If you sell the house in the next 50 years, you will never see a profit.'
                : 'Selling the property at ' +
                  breakEvenYear +
                  ' or later will result in a profit.'
            }
            actionText="Go to graph"
            backgroundColor={colors.Dark1Color}
            iconBackgroundColor="#a6c0ed"
            onClickActionText={() => setActiveStep(1)}
            className="bg-dark-tertiary text-input-primary"
            iconClassName="icon-accent"
          />
        </Grid>

        <Grid item xs={3} className="tile-cont">
          <DataTile
            iconVariant={IconType.UpArrow}
            iconColor="white"
            title={`$${perMonthRentNeeded.toLocaleString()}`}
            subtitle="Break Even Rent"
            description={`You will need to rent the property for $${perMonthRentNeeded.toLocaleString()} per month to cover costs in the first 5 years.`}
            backgroundColor={colors.Dark1Color}
            iconBackgroundColor="#a6c0ed"
            className="bg-dark-tertiary text-input-primary"
            iconClassName="icon-accent"
          />
        </Grid>

        <Grid item xs={3} className="tile-cont">
          <DataTile
            iconVariant={IconType.RequestQuoteIcon}
            iconColor="white"
            title={`$${upfrontCosts.toLocaleString()}`}
            subtitle="Upfront Costs"
            description={`You will pay $${upfrontCosts.toLocaleString()} in upfront costs and $${downPayment.toLocaleString()} as a down payment when purchasing the property.`}
            actionText="Go to graph"
            backgroundColor={colors.Dark1Color}
            iconBackgroundColor="#a6c0ed"
            onClickActionText={() => setActiveStep(3)}
            className="bg-dark-tertiary text-input-primary"
            iconClassName="icon-accent"
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default OverviewChart;
